import { HL_MAINTENANCE_MARGIN_RATIO } from "@src/features/trade/hyperliquid/constants/config"
import type {
  HLOpenOrder,
  HLPosition,
} from "@src/features/trade/hyperliquid/types"
import { fmtPrice } from "./format"

export function calculatePositionPnl(params: {
  entryPrice: number
  targetPrice: number
  positionSize: number
  isLong: boolean
}) {
  return params.isLong
    ? (params.targetPrice - params.entryPrice) * params.positionSize
    : (params.entryPrice - params.targetPrice) * params.positionSize
}

export function calculateRoePercent(params: { pnl: number; margin: number }) {
  return params.margin > 0 ? (params.pnl / params.margin) * 100 : 0
}

export function estimateLiquidationPrice(params: {
  currentPrice: number
  leverage: number
  isLong: boolean
}) {
  if (params.currentPrice <= 0 || params.leverage <= 0) {
    return 0
  }

  return params.isLong
    ? params.currentPrice *
        (1 - 1 / params.leverage + HL_MAINTENANCE_MARGIN_RATIO)
    : params.currentPrice *
        (1 + 1 / params.leverage - HL_MAINTENANCE_MARGIN_RATIO)
}

export function formatPriceDistanceFromCurrent(params: {
  price: number
  currentPrice: number
}) {
  if (params.price <= 0 || params.currentPrice <= 0) return "-"

  const distancePercent =
    (Math.abs(params.price - params.currentPrice) / params.currentPrice) * 100
  const distanceLabel = distancePercent.toFixed(1).replace(/\.0$/, "")
  const direction = params.price >= params.currentPrice ? "above" : "below"

  return `${distanceLabel}% ${direction}`
}

export function calculateTriggerPriceFromPnlPercent(params: {
  pnlPercent: number
  entryPrice: number
  leverage: number
  isLong: boolean
  leg: "tp" | "sl"
}) {
  if (
    params.pnlPercent <= 0 ||
    params.entryPrice <= 0 ||
    params.leverage <= 0
  ) {
    return 0
  }

  const priceDelta =
    (params.entryPrice * (params.pnlPercent / 100)) / params.leverage

  if (params.leg === "tp") {
    return params.isLong
      ? params.entryPrice + priceDelta
      : params.entryPrice - priceDelta
  }

  return params.isLong
    ? params.entryPrice - priceDelta
    : params.entryPrice + priceDelta
}

export function findPositionAutoCloseOrders(params: {
  coin: string
  isLong: boolean
  openOrders?: HLOpenOrder[]
}) {
  const coinOrders = getPositionAutoCloseOrders(params)

  return {
    tpOrder: findNewestOrder(coinOrders, isTakeProfitOrder),
    slOrder: findNewestOrder(coinOrders, isStopLossOrder),
  }
}

export function getPositionAutoCloseOrders(params: {
  coin: string
  isLong: boolean
  openOrders?: HLOpenOrder[]
}) {
  const closeSide = params.isLong ? "A" : "B"
  return (
    params.openOrders?.filter(
      (order) =>
        order.coin === params.coin &&
        order.reduceOnly &&
        order.isTrigger &&
        order.side === closeSide &&
        (isTakeProfitOrder(order) || isStopLossOrder(order))
    ) ?? []
  )
}

export function buildPositionAutoCloseSummary(params: {
  order: HLOpenOrder
  position: HLPosition
}) {
  const triggerPrice = Number(params.order.triggerPx)
  const entryPrice = Number(params.position.entryPx)
  const leverage = Number(params.position.leverage.value)
  const isLong = Number(params.position.szi) > 0
  const priceMovePercent =
    entryPrice > 0
      ? isLong
        ? ((triggerPrice - entryPrice) / entryPrice) * 100
        : ((entryPrice - triggerPrice) / entryPrice) * 100
      : 0
  const pnlPercent = leverage > 0 ? priceMovePercent * leverage : 0

  return {
    priceLabel: fmtPrice(triggerPrice),
    secondaryLabel: `${pnlPercent >= 0 ? "+" : ""}${pnlPercent.toFixed(1)}%`,
  }
}

function isTakeProfitOrder(order: HLOpenOrder) {
  return (
    order.orderType === "Take Profit Market" ||
    order.orderType === "Take Profit Limit"
  )
}

function isStopLossOrder(order: HLOpenOrder) {
  return order.orderType === "Stop Market" || order.orderType === "Stop Limit"
}

function findNewestOrder(
  orders: HLOpenOrder[],
  predicate: (order: HLOpenOrder) => boolean
) {
  return orders
    .filter(predicate)
    .sort((a, b) => b.timestamp - a.timestamp || b.oid - a.oid)[0]
}
